import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock, MapPin, Phone, Package, ClipboardList, CheckCircle2, AlertCircle, RotateCcw, User, ArrowRight, FileText, Download, XCircle } from "lucide-react";

const DAYS_IT = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];

const formatDay = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    return `${DAYS_IT[d.getDay()]} ${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
  } catch { return iso; }
};

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function MieiAppuntamenti() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [koReasons, setKoReasons] = useState([]);
  const [koAssignment, setKoAssignment] = useState(null); // assegnazione in fase di KO
  const [koReason, setKoReason] = useState("");
  const [koNote, setKoNote] = useState("");
  const [koBusy, setKoBusy] = useState(false);

  const loadDates = async () => {
    const { data } = await api.get("/assignments/dates");
    setDates(data);
    if (data.length) {
      const today = todayIso();
      const upcoming = data.find((d) => d.date >= today) || data[data.length - 1];
      setSelectedDate(upcoming.date);
    }
  };

  const loadDay = async (d) => {
    if (!d) { setAssignments([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/assignments", { params: { date: d } });
      setAssignments(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadDates(); }, []);
  useEffect(() => { loadDay(selectedDate); }, [selectedDate]);
  useEffect(() => {
    api.get("/assignments/ko-reasons").then((r) => setKoReasons(r.data?.reasons || [])).catch(() => {});
  }, []);

  const openKo = (a) => { setKoAssignment(a); setKoReason(""); setKoNote(""); };
  const closeKo = () => { setKoAssignment(null); setKoReason(""); setKoNote(""); };

  const submitKo = async () => {
    if (!koReason) { toast.error("Seleziona una motivazione"); return; }
    setKoBusy(true);
    try {
      await api.post(`/assignments/${koAssignment.id}/mark-ko`, { reason: koReason, note: koNote });
      toast.success("Impianto segnato come KO");
      closeKo();
      loadDay(selectedDate);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setKoBusy(false); }
  };

  const toggleDone = async (a) => {
    const newStatus = a.status === "done" ? "pending" : "done";
    try {
      await api.patch(`/assignments/${a.id}`, { status: newStatus });
      setAssignments((prev) => prev.map((x) => (x.id === a.id ? { ...x, status: newStatus } : x)));
      toast.success(newStatus === "done" ? "Segnato come fatto" : "Ripristinato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const stats = useMemo(() => ({
    total: assignments.length,
    done: assignments.filter((a) => a.status === "done").length,
  }), [assignments]);

  const downloadAttachment = async (aid, att) => {
    try {
      const res = await api.get(`/assignments/${aid}/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "file"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.agenda_tecnico")}</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.my_appointments")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("auto.appuntamenti_assegnati_dal_caposquadra_ordinati_per_orario")}</p>
      </div>

      {dates.length === 0 && (
        <div className="rounded-sm border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">
          Non hai appuntamenti assegnati. Il caposquadra deve ancora caricare il file Excel del mese.
        </div>
      )}

      {dates.length > 0 && (
        <>
          {/* Selettore giorni orizzontale scrollabile */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {dates.map((d) => {
              const isSel = d.date === selectedDate;
              const isToday = d.date === todayIso();
              const isPast = d.date < todayIso();
              return (
                <button
                  key={d.date}
                  onClick={() => setSelectedDate(d.date)}
                  data-testid={`tech-day-${d.date}`}
                  className={`flex min-w-[130px] flex-shrink-0 flex-col items-center gap-1 rounded-sm border-2 px-3 py-2 transition-all ${
                    isSel
                      ? "border-[#0052FF] bg-[#0052FF] text-white shadow-md"
                      : isToday
                      ? "border-emerald-400 bg-emerald-50 text-emerald-900 hover:border-emerald-600"
                      : isPast
                      ? "border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                  }`}
                >
                  <div className={`text-[10px] font-bold uppercase tracking-wider ${isSel ? "opacity-80" : ""}`}>
                    {formatDay(d.date).split(" ")[0]}{isToday && !isSel && " · OGGI"}
                  </div>
                  <div className={`font-mono text-lg font-bold ${isSel ? "" : ""}`}>
                    {d.date.slice(8, 10)}/{d.date.slice(5, 7)}
                  </div>
                  <Badge className={`rounded-sm text-[10px] ${isSel ? "bg-white text-[#0052FF]" : "bg-white/80 text-slate-700"}`}>
                    {d.pending}/{d.count}
                  </Badge>
                </button>
              );
            })}
          </div>

          {/* Header giornata */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#0052FF]" />
              <div>
                <div className="text-xl font-bold text-slate-900">{formatDay(selectedDate)}</div>
                <div className="text-xs text-slate-500">{stats.done} completati su {stats.total}</div>
              </div>
            </div>
            <div className="flex-1 max-w-md">
              <div className="h-2 rounded-full bg-slate-100">
                <div
                  className="h-2 rounded-full bg-emerald-500 transition-all"
                  style={{ width: `${stats.total > 0 ? (stats.done / stats.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>

          {/* Appuntamenti */}
          <div className="space-y-3">
            {loading && <div className="rounded-sm border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">{t("auto.caricamento")}</div>}
            {!loading && assignments.length === 0 && <div className="rounded-sm border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">{t("auto.nessun_appuntamento_in_questa_giornata")}</div>}
            {!loading && assignments.map((a) => (
              <div
                key={a.id}
                className={`rounded-sm border p-4 transition-all ${
                  a.status === "done"
                    ? "border-emerald-200 bg-emerald-50/50 opacity-70"
                    : a.status === "ko"
                    ? "border-red-200 bg-red-50/50 opacity-80"
                    : "border-slate-200 bg-white shadow-sm hover:shadow-md cursor-pointer"
                }`}
                data-testid={`my-assignment-${a.id}`}
                onClick={() => {
                  if (a.status === "done" || a.status === "ko") return;
                  const params = new URLSearchParams({
                    assignment_id: a.id,
                    ...(a.intervention_ref ? { intervention_ref: a.intervention_ref } : {}),
                    ...(a.customer ? { customer: a.customer } : {}),
                    ...(a.address ? { address: a.address } : {}),
                    ...(a.sheet_name ? { sheet_name: a.sheet_name } : {}),
                  });
                  navigate(`/install?${params.toString()}`);
                }}
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className={`flex min-w-[90px] flex-col items-center rounded-sm px-3 py-2 ${a.status === "done" ? "bg-emerald-600 text-white" : a.status === "ko" ? "bg-red-600 text-white" : "bg-slate-900 text-white"}`}>
                    <Clock className="h-3.5 w-3.5 opacity-80" />
                    <div className="font-mono text-lg font-bold">{a.time || "—"}</div>
                  </div>
                  <div className="flex-1 space-y-1.5 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-[#0052FF]" />
                      <span className="font-mono text-sm font-bold uppercase tracking-wider text-slate-900">IMP #{a.intervention_ref || "—"}</span>
                      {a.wr_ref && <span className="font-mono text-xs font-bold text-slate-500">· WR {a.wr_ref}</span>}
                      {a.external_status && <Badge className="rounded-sm bg-slate-800 text-white text-[10px] uppercase">{a.external_status}</Badge>}
                      {a.status === "ko" && (
                        <Badge className="rounded-sm bg-red-600 text-white text-[10px] uppercase">
                          <XCircle className="mr-1 h-3 w-3" /> KO · {a.ko_reason || "—"}
                        </Badge>
                      )}
                    </div>
                    {a.material && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-700"><Package className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" /> <span className="break-words">{a.material}</span></div>
                    )}
                    {a.customer && (
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><User className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" /> <span className="break-words">{a.customer}</span></div>
                    )}
                    {(a.address || a.city) && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-700"><MapPin className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" /> {a.address}{a.city && <span className="text-slate-500">, {a.city}</span>}</div>
                    )}
                    {a.phone && (
                      <div className="flex items-center gap-1.5 text-sm"><Phone className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" /> <a href={`tel:${a.phone}`} className="font-mono font-semibold text-[#0052FF] hover:underline">{a.phone}</a></div>
                    )}
                    {a.notes && (
                      <div className="mt-1 flex items-start gap-1.5 rounded-sm bg-amber-50 px-2 py-1 text-xs text-amber-900"><AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" /> {a.notes}</div>
                    )}
                    {(a.attachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-sm border border-blue-100 bg-blue-50/60 px-2 py-1.5">
                        <FileText className="h-3.5 w-3.5 text-[#0052FF]" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0052FF]">Allegati</span>
                        {a.attachments.map((att) => (
                          <button
                            key={att.id}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); downloadAttachment(a.id, att); }}
                            className="inline-flex max-w-[220px] items-center gap-1 rounded-sm border border-blue-200 bg-white px-2 py-0.5 text-xs font-medium text-[#0052FF] hover:bg-blue-100"
                            data-testid={`my-attachment-${a.id}-${att.id}`}
                            title={att.filename}
                          >
                            <Download className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{att.filename}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); toggleDone(a); }}
                      className={`rounded-sm ${a.status === "done" ? "bg-slate-200 text-slate-700 hover:bg-slate-300" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                      data-testid={`my-assignment-toggle-${a.id}`}
                      disabled={a.status === "ko"}
                    >
                      {a.status === "done" ? (<><RotateCcw className="mr-1 h-3.5 w-3.5" /> Ripristina</>) : (<><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Fatto</>)}
                    </Button>
                    {a.status !== "done" && a.status !== "ko" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); openKo(a); }}
                        className="rounded-sm border-red-300 bg-white text-red-700 hover:bg-red-50"
                        data-testid={`my-assignment-ko-${a.id}`}
                      >
                        <XCircle className="mr-1 h-3.5 w-3.5" /> Segna KO
                      </Button>
                    )}
                  </div>
                </div>
                {a.status === "ko" && a.ko_note && (
                  <div className="mt-2 rounded-sm bg-red-50 px-2 py-1 text-xs text-red-900" data-testid={`ko-note-${a.id}`}>
                    <b>Nota KO:</b> {a.ko_note}
                  </div>
                )}
                {a.status !== "done" && a.status !== "ko" && (
                  <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-2 text-xs text-[#0052FF]">
                    <span>Clicca l'impianto per aprire la pre-chiusura</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* KO Modal */}
      <Dialog open={!!koAssignment} onOpenChange={(o) => { if (!o) closeKo(); }}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle>Segna Impianto KO</DialogTitle>
          </DialogHeader>
          {koAssignment && (
            <div className="space-y-3 text-sm">
              <div className="rounded-sm bg-slate-50 p-2 text-xs">
                <div><b>Impianto:</b> {koAssignment.intervention_ref || "—"}</div>
                {koAssignment.customer && <div><b>Cliente:</b> {koAssignment.customer}</div>}
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Motivazione *</label>
                <Select value={koReason} onValueChange={setKoReason}>
                  <SelectTrigger className="rounded-sm" data-testid="ko-reason-select"><SelectValue placeholder="Seleziona motivazione" /></SelectTrigger>
                  <SelectContent>
                    {koReasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Nota (opzionale)</label>
                <Textarea value={koNote} onChange={(e) => setKoNote(e.target.value)} placeholder="Es. cliente ha spostato l'appuntamento…" rows={3} className="rounded-sm text-sm" data-testid="ko-note" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeKo} className="rounded-sm">Annulla</Button>
            <Button onClick={submitKo} disabled={koBusy || !koReason} className="rounded-sm bg-red-600 text-white hover:bg-red-700" data-testid="ko-submit">
              {koBusy ? "Salvataggio…" : "Segna KO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
